import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  WeddingInvitation, 
  InvitationFamily, 
  InvitationGuest, 
  InvitationGallery, 
  InvitationGift,
  GuestPhoto,
  ItineraryItem
} from '@/types/invitation';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import { ensureEventId } from '@/hooks/useEvent';

// Helper to generate unique slug
const generateSlug = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// 008 · US2 — el código de acceso lo genera la BD (generate_access_code, CSPRNG); ya no en el cliente.

// Transform DB row to WeddingInvitation type
const transformInvitation = (row: Record<string, unknown>): WeddingInvitation => ({
  ...row,
  itinerary: (row.itinerary as ItineraryItem[]) || [],
} as WeddingInvitation);

// Fetch user's invitations
export function useUserInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_invitations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []).map(transformInvitation);
    },
  });
}

// Fetch single invitation
export function useInvitation(id: string) {
  return useQuery({
    queryKey: ['invitation', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_invitations')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data ? transformInvitation(data as Record<string, unknown>) : null;
    },
    enabled: !!id,
  });
}

// Fetch invitation by slug (public)
export function useInvitationBySlug(slug: string) {
  return useQuery({
    queryKey: ['invitation-public', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_invitations')
        .select('*')
        .eq('unique_slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      
      if (error) throw error;
      return data ? transformInvitation(data as Record<string, unknown>) : null;
    },
    enabled: !!slug,
  });
}

// Helper to convert empty strings to null for time fields
const emptyToNull = (value: string | undefined): string | null => {
  return value === '' || value === undefined ? null : value;
};

const DEFAULT_FIRST_INVITATION_TASKS = [
  'Reservar salón',
  'Definir invitados',
  'Elegir catering',
  'Comprar vestido/traje',
  'Comprar anillos',
];

// Create invitation
export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invData: Partial<WeddingInvitation>) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('No autenticado');

      // 006: el Evento (weddings) es la fuente única. Garantizamos que exista y
      // sincronizamos nombres/fecha en él (la invitación queda como presentación).
      const eventId = await ensureEventId(userData.user.id);
      await supabase
        .from('weddings')
        .update({
          bride_name: invData.bride_name || null,
          groom_name: invData.groom_name || null,
          wedding_date: invData.wedding_date || null,
          estilo: invData.template_id || null,
        })
        .eq('id', eventId);

      const insertData = {
        event_id: eventId,
        bride_name: invData.bride_name || '',
        groom_name: invData.groom_name || '',
        wedding_date: invData.wedding_date || new Date().toISOString(),
        template_id: invData.template_id || 'romantic',
        primary_color: invData.primary_color,
        secondary_color: invData.secondary_color,
        accent_color: invData.accent_color,
        religious_ceremony_enabled: invData.religious_ceremony_enabled,
        religious_ceremony_name: emptyToNull(invData.religious_ceremony_name),
        religious_ceremony_address: emptyToNull(invData.religious_ceremony_address),
        religious_ceremony_time: emptyToNull(invData.religious_ceremony_time),
        religious_ceremony_map_url: emptyToNull(invData.religious_ceremony_map_url),
        civil_ceremony_enabled: invData.civil_ceremony_enabled,
        civil_ceremony_name: emptyToNull(invData.civil_ceremony_name),
        civil_ceremony_address: emptyToNull(invData.civil_ceremony_address),
        civil_ceremony_time: emptyToNull(invData.civil_ceremony_time),
        civil_ceremony_map_url: emptyToNull(invData.civil_ceremony_map_url),
        party_name: emptyToNull(invData.party_name),
        party_address: emptyToNull(invData.party_address),
        party_time: emptyToNull(invData.party_time),
        party_map_url: emptyToNull(invData.party_map_url),
        dresscode: emptyToNull(invData.dresscode),
        itinerary: (invData.itinerary || []) as unknown as Json,
        background_music_url: emptyToNull(invData.background_music_url),
        background_music_enabled: invData.background_music_enabled ?? true,
        children_allowed: invData.children_allowed ?? true,
        gifts_message: emptyToNull(invData.gifts_message),
        bank_transfer_enabled: invData.bank_transfer_enabled,
        bank_account_holder: emptyToNull(invData.bank_account_holder),
        bank_account_number: emptyToNull(invData.bank_account_number),
        bank_name: emptyToNull(invData.bank_name),
        cash_envelope_enabled: invData.cash_envelope_enabled,
        gift_registry_url: emptyToNull(invData.gift_registry_url),
        couple_gallery_enabled: invData.couple_gallery_enabled ?? true,
        guest_photos_enabled: invData.guest_photos_enabled ?? true,
        unique_slug: generateSlug(),
        is_published: invData.is_published ?? false,
        user_id: userData.user.id,
      };
      
      const { data: result, error } = await supabase
        .from('wedding_invitations')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const invitation = transformInvitation(result as Record<string, unknown>);

      // Seed default tasks on the user's first invitation (best-effort).
      const { count, error: countError } = await supabase
        .from('wedding_invitations')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userData.user.id);

      if (countError) {
        console.warn('No se pudo verificar el conteo de invitaciones para seed de tareas:', countError);
      } else if (count === 1) {
        const seedRows = DEFAULT_FIRST_INVITATION_TASKS.map((title) => ({
          user_id: userData.user!.id,
          event_id: eventId,
          invitation_id: invitation.id,
          title,
          completed: false,
        }));
        const { error: seedError } = await supabase.from('wedding_tasks').insert(seedRows);
        if (seedError) {
          console.warn('No se pudieron sembrar las tareas iniciales:', seedError);
        }
      }

      return invitation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Invitación creada exitosamente');
    },
    onError: (error) => {
      toast.error('Error al crear la invitación: ' + error.message);
    },
  });
}

// Update invitation
export function useUpdateInvitation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WeddingInvitation> }) => {
      const updateData: Record<string, unknown> = {};

      // Process each field, converting empty strings to null for time and optional text fields
      const timeFields = ['religious_ceremony_time', 'civil_ceremony_time', 'party_time'];
      const optionalTextFields = [
        'religious_ceremony_name', 'religious_ceremony_address', 'religious_ceremony_map_url',
        'civil_ceremony_name', 'civil_ceremony_address', 'civil_ceremony_map_url',
        'party_name', 'party_address', 'party_map_url',
        'dresscode', 'background_music_url', 'gifts_message',
        'bank_account_holder', 'bank_account_number', 'bank_name', 'gift_registry_url'
      ];
      // 006: nombres/fecha son del Evento (fuente única). El editor/autosave de la
      // invitación NO debe reintroducir la duplicación → se ignoran aquí.
      const eventOwnedFields = ['bride_name', 'groom_name', 'wedding_date', 'event_id', 'archived'];

      for (const [key, value] of Object.entries(data)) {
        if (eventOwnedFields.includes(key)) {
          continue;
        } else if (key === 'itinerary') {
          updateData[key] = value as unknown as Json;
        } else if (timeFields.includes(key) || optionalTextFields.includes(key)) {
          updateData[key] = emptyToNull(value as string);
        } else {
          updateData[key] = value;
        }
      }
      
      const { data: result, error } = await supabase
        .from('wedding_invitations')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return transformInvitation(result as Record<string, unknown>);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['invitation', variables.id] });
      toast.success('Invitación actualizada');
    },
    onError: (error) => {
      toast.error('Error al actualizar: ' + error.message);
    },
  });
}

// Delete invitation
export function useDeleteInvitation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('wedding_invitations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitación eliminada');
    },
    onError: (error) => {
      toast.error('Error al eliminar: ' + error.message);
    },
  });
}

// Family members
export function useInvitationFamily(invitationId: string) {
  return useQuery({
    queryKey: ['invitation-family', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_family')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('display_order');
      
      if (error) throw error;
      return data as InvitationFamily[];
    },
    enabled: !!invitationId,
  });
}

export function useAddFamilyMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { invitation_id: string; name: string; role: string; display_order?: number }) => {
      const { data: result, error } = await supabase
        .from('invitation_family')
        .insert({
          invitation_id: data.invitation_id,
          name: data.name,
          role: data.role,
          display_order: data.display_order ?? 0,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as InvitationFamily;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-family', variables.invitation_id] });
      toast.success('Familiar agregado');
    },
  });
}

export function useDeleteFamilyMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, invitationId }: { id: string; invitationId: string }) => {
      const { error } = await supabase
        .from('invitation_family')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-family', invitationId] });
      toast.success('Familiar eliminado');
    },
  });
}

// Guests
export function useInvitationGuests(invitationId: string) {
  return useQuery({
    queryKey: ['invitation-guests', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_guests')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as InvitationGuest[];
    },
    enabled: !!invitationId,
  });
}

export function useAddGuest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { invitation_id: string; guest_name: string; guest_email?: string; guest_phone?: string; passes_count?: number }) => {
      const { data: result, error } = await supabase
        .from('invitation_guests')
        .insert({
          invitation_id: data.invitation_id,
          guest_name: data.guest_name,
          guest_email: data.guest_email,
          guest_phone: data.guest_phone,
          passes_count: data.passes_count ?? 1,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as InvitationGuest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-guests', variables.invitation_id] });
      toast.success('Invitado agregado');
    },
  });
}

export function useUpdateGuest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data, invitationId }: { id: string; data: Partial<InvitationGuest>; invitationId: string }) => {
      const { data: result, error } = await supabase
        .from('invitation_guests')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { result: result as InvitationGuest, invitationId };
    },
    onSuccess: ({ invitationId }) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-guests', invitationId] });
    },
  });
}

export function useDeleteGuest() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, invitationId }: { id: string; invitationId: string }) => {
      const { error } = await supabase
        .from('invitation_guests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-guests', invitationId] });
      toast.success('Invitado eliminado');
    },
  });
}

// Type for the RPC response
interface GuestAccessResponse {
  id: string;
  invitation_id: string;
  guest_name: string;
  display_label: string | null;
  passes_count: number;
  rsvp_status: string;
  rsvp_guests_count: number;
  rsvp_dietary_restrictions: string | null;
  rsvp_message: string | null;
  rsvp_responded_at: string | null;
  rsvp_deadline: string | null;
  coordinator_name: string | null;
  coordinator_phone: string | null;
}

// 008 · US7 — marca la invitación como "vista" (fire-and-forget, anónimo, best-effort).
export async function markGuestViewed(accessCode: string, seconds?: number, section?: string) {
  if (!accessCode) return;
  try {
    await supabase.rpc('mark_guest_viewed', {
      p_access_code: accessCode,
      p_seconds: seconds,
      p_section: section,
    });
  } catch {
    // best-effort: el tracking no debe afectar la experiencia del invitado
  }
}

// Guest by access code (public) - uses secure RPC function
export function useGuestByAccessCode(accessCode: string) {
  return useQuery({
    queryKey: ['guest-access', accessCode],
    queryFn: async () => {
      if (!accessCode) return null;
      
      // Use secure RPC function that validates access code
      const { data, error } = await supabase
        .rpc('get_guest_by_access_code', { p_access_code: accessCode });
      
      if (error) throw error;
      if (!data) return null;
      
      // Cast the JSONB response
      const guestData = data as unknown as GuestAccessResponse;
      
      // Transform to InvitationGuest type
      return {
        id: guestData.id,
        invitation_id: guestData.invitation_id,
        guest_name: guestData.guest_name,
        display_label: guestData.display_label,
        passes_count: guestData.passes_count,
        rsvp_status: guestData.rsvp_status,
        rsvp_guests_count: guestData.rsvp_guests_count,
        rsvp_dietary_restrictions: guestData.rsvp_dietary_restrictions,
        rsvp_message: guestData.rsvp_message,
        rsvp_responded_at: guestData.rsvp_responded_at,
        rsvp_deadline: guestData.rsvp_deadline,
        coordinator_name: guestData.coordinator_name,
        coordinator_phone: guestData.coordinator_phone,
        // These fields are not exposed for public access
        guest_email: undefined,
        guest_phone: undefined,
        unique_access_code: accessCode,
        created_at: '',
        updated_at: '',
      } as InvitationGuest;
    },
    enabled: !!accessCode,
  });
}

// Update RSVP (public) - uses secure RPC function with access code validation
export function useUpdateRSVP() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ accessCode, data }: { accessCode: string; data: Partial<InvitationGuest> }) => {
      // Use secure RPC function that validates access code server-side
      const { data: result, error } = await supabase
        .rpc('update_guest_rsvp', {
          p_access_code: accessCode,
          p_rsvp_status: data.rsvp_status || 'pending',
          p_guests_count: data.rsvp_guests_count ?? 0,
          p_dietary_restrictions: data.rsvp_dietary_restrictions || null,
          p_message: data.rsvp_message || null,
        });
      
      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['guest-access', variables.accessCode] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['invitation-guests'] });
      toast.success('Confirmación guardada');
    },
    onError: (error) => {
      // 008 · US1 — mensajes amigables para el invitado (no exponer códigos técnicos).
      const raw = error.message || '';
      if (raw.includes('rsvp_closed')) {
        toast.error(
          'El plazo para confirmar tu asistencia ya cerró. Por favor, escríbeles directamente a los novios para coordinar.',
        );
      } else if (raw.includes('rate_limited')) {
        toast.error('Hubo demasiados intentos. Espera un momento e inténtalo de nuevo.');
      } else if (raw.includes('exceed') || raw.includes('passes')) {
        toast.error('El número de asistentes supera los lugares asignados a tu invitación.');
      } else {
        toast.error('No pudimos guardar tu confirmación. Inténtalo de nuevo o escríbeles a los novios.');
      }
    },
  });
}

// Gallery
export function useInvitationGallery(invitationId: string) {
  return useQuery({
    queryKey: ['invitation-gallery', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_gallery')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('display_order');
      
      if (error) throw error;
      return data as InvitationGallery[];
    },
    enabled: !!invitationId,
  });
}

export function useAddGalleryPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { invitation_id: string; photo_url: string; caption?: string; display_order?: number }) => {
      const { data: result, error } = await supabase
        .from('invitation_gallery')
        .insert({
          invitation_id: data.invitation_id,
          photo_url: data.photo_url,
          caption: data.caption,
          display_order: data.display_order ?? 0,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as InvitationGallery;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-gallery', variables.invitation_id] });
      toast.success('Foto agregada');
    },
  });
}

export function useDeleteGalleryPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, invitationId }: { id: string; invitationId: string }) => {
      const { error } = await supabase
        .from('invitation_gallery')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-gallery', invitationId] });
      toast.success('Foto eliminada');
    },
  });
}

// Gifts
export function useInvitationGifts(invitationId: string) {
  return useQuery({
    queryKey: ['invitation-gifts', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_gifts')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('created_at');
      
      if (error) throw error;
      return data as InvitationGift[];
    },
    enabled: !!invitationId,
  });
}

export function useAddGift() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { 
      invitation_id: string; 
      gift_name: string; 
      gift_description?: string;
      gift_image_url?: string;
      gift_price?: number;
      store_name?: string;
      store_url?: string;
    }) => {
      const { data: result, error } = await supabase
        .from('invitation_gifts')
        .insert({
          invitation_id: data.invitation_id,
          gift_name: data.gift_name,
          gift_description: data.gift_description,
          gift_image_url: data.gift_image_url,
          gift_price: data.gift_price,
          store_name: data.store_name,
          store_url: data.store_url,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as InvitationGift;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-gifts', variables.invitation_id] });
      toast.success('Regalo agregado');
    },
  });
}

export function useDeleteGift() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, invitationId }: { id: string; invitationId: string }) => {
      const { error } = await supabase
        .from('invitation_gifts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      queryClient.invalidateQueries({ queryKey: ['invitation-gifts', invitationId] });
      toast.success('Regalo eliminado');
    },
  });
}

// Guest photos
export function useGuestPhotos(invitationId: string, approvedOnly = false) {
  return useQuery({
    queryKey: ['guest-photos', invitationId, approvedOnly],
    queryFn: async () => {
      let query = supabase
        .from('guest_photos')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('created_at', { ascending: false });
      
      if (approvedOnly) {
        query = query.eq('is_approved', true);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as GuestPhoto[];
    },
    enabled: !!invitationId,
  });
}

export function useUploadGuestPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { invitation_id: string; photo_url: string; guest_id?: string; caption?: string }) => {
      const { data: result, error } = await supabase
        .from('guest_photos')
        .insert({
          invitation_id: data.invitation_id,
          photo_url: data.photo_url,
          guest_id: data.guest_id,
          caption: data.caption,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as GuestPhoto;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['guest-photos', variables.invitation_id] });
      toast.success('Foto subida, pendiente de aprobación');
    },
  });
}

export function useApproveGuestPhoto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, approved, invitationId }: { id: string; approved: boolean; invitationId: string }) => {
      const { error } = await supabase
        .from('guest_photos')
        .update({ is_approved: approved })
        .eq('id', id);
      
      if (error) throw error;
      return invitationId;
    },
    onSuccess: (invitationId) => {
      queryClient.invalidateQueries({ queryKey: ['guest-photos', invitationId] });
      toast.success('Foto actualizada');
    },
  });
}

// Public family (for invitation view)
export function usePublicInvitationFamily(invitationId: string) {
  return useQuery({
    queryKey: ['public-invitation-family', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_family')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('display_order');
      
      if (error) throw error;
      return data as InvitationFamily[];
    },
    enabled: !!invitationId,
  });
}

// Public gallery
export function usePublicInvitationGallery(invitationId: string) {
  return useQuery({
    queryKey: ['public-invitation-gallery', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_gallery')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('display_order');
      
      if (error) throw error;
      return data as InvitationGallery[];
    },
    enabled: !!invitationId,
  });
}

// Public gifts
export function usePublicInvitationGifts(invitationId: string) {
  return useQuery({
    queryKey: ['public-invitation-gifts', invitationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitation_gifts')
        .select('*')
        .eq('invitation_id', invitationId)
        .order('created_at');
      
      if (error) throw error;
      return data as InvitationGift[];
    },
    enabled: !!invitationId,
  });
}
